import { memo, useCallback, useMemo } from 'react';
import { cn } from '@divlab/divanui';

import * as PickPairStore from '@Stores/PickPair';
import * as Cart from '@Queries/Cart';
import * as PageProductStore from '@Stores/PageProduct';
import * as ModalWindows from '@Stores/ModalWindows';
import useSearchParams from '@Hooks/useSearchParams';
import { useMeta } from '@Queries/Meta';
import useNavigation from '@Navigation/useNavigation';
import useTranslation from '@Queries/useTranslation';
import Button from '@UI/Button';
import styles from './BuyButton.module.css';

import type { ButtonProps } from '@UI/Button';
import type { FC } from 'react';
import type { ProductData } from '@Types/Product';

export interface BuyButtonProps extends ButtonProps {
  className?: string;
  product: ProductData;
}

const BuyButton: FC<BuyButtonProps> = (props) => {
  const { className, product, ...restProps } = props;
  const updateSet = Cart.useUpdatePositionInCart();
  const { t } = useTranslation();
  const { openPage } = useNavigation();
  const meta = useMeta();
  const { region } = meta.data;
  const shopProducts = PickPairStore.useSelectedSet();
  const parameterValues = PageProductStore.getParameterValues();
  const hasWarnings = PickPairStore.useHasWarnings();
  const searchParams = useSearchParams();
  const oldHash = searchParams.get('hash');
  const addToCart = Cart.useAddToCart();

  const shopProduct = useMemo(
    () => ({
      shopProductId: product.id,
      parameterValues,
      shopProducts,
    }),
    [parameterValues, product.id, shopProducts],
  );

  const hasInCart = Cart.useHasInCart(shopProduct);

  const handleError = useCallback(() => {
    ModalWindows.open('Info', { view: 'error', variant: 'unknown-error' });
  }, []);

  const handleClick = useCallback(() => {
    if (hasWarnings) return;

    if (hasInCart) {
      openPage({ url: `${region.url}/order/check` });
      return;
    }

    const products = [shopProduct];

    if (oldHash) {
      if (!updateSet.isPending && !updateSet.isSuccess) {
        updateSet.mutate(
          { products, options: { isRelated: false }, oldHash },
          {
            onError: handleError,
            onSuccess: () => openPage({ url: `${region.url}/order/check` }),
          },
        );
      }
      return;
    }

    if (addToCart.isPending || addToCart.isError) {
      return;
    }

    addToCart.mutate(
      {
        products,
        options: { isRelated: false },
        analyticsData: {
          products: [product],
          label: 'Конфигуратор',
        },
      },
      {
        onError: handleError,
      },
    );

    // ModalWindows.open('Cart', { products, analyticsLabel: 'Конфигуратор' });
  }, [
    hasWarnings,
    hasInCart,
    shopProduct,
    oldHash,
    addToCart,
    product,
    handleError,
    openPage,
    region.url,
    updateSet,
  ]);

  return hasInCart ? (
    <Button
      {...restProps}
      wide
      className={cn(styles.buyButton, styles.sizeBig, { [styles.hasInCart]: hasInCart }, className)}
      theme='transparent'
      data-testid='sidebar-buy-button'
      onClick={handleClick}
    >
      <div className={styles.iconCheck} />
      {t('ui.in-cart')}
    </Button>
  ) : (
    <Button
      {...restProps}
      wide
      className={cn(styles.buyButton, styles.sizeBig, className)}
      data-testid='sidebar-buy-button'
      waiting={oldHash ? updateSet.isPending : addToCart.isPending}
      onClick={handleClick}
    >
      {oldHash ? t('ui.save-changes') : t('ui.add-to-cart')}
    </Button>
  );
};

export default memo(BuyButton);
